import { Icon, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  DEFAULT_DATE_FORMAT_FNS,
  getTypedObjectKeys,
  GroupType,
  GroupWithMembers,
  SchoolProperty,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import {
  AgeGroupSelect,
  AgeGroupSelectMultiple,
  SchoolPeriodsRangeSelect,
  SchoolPeriodsRangeSelectComponent,
} from '@schooly/components/filters';
import { DATE_FORMAT, SchoolUserRole } from '@schooly/constants';
import { DROPDOWN, GROUP } from '@schooly/constants/test-ids';
import {
  Attention2Icon,
  CrossIcon,
  InformationIcon,
  Loading,
  LockIcon,
  MinusIcon,
  ModalSmall,
  MoreIcon,
  WithShowOnClick,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { mobileDetect } from '@schooly/utils/mobile';
import { format } from 'date-fns';
import isEqual from 'lodash.isequal';
import React, { useCallback, useMemo, useRef } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { ContextMenu } from '../../../components/common/ContextMenu/ContextMenu';
import { PersonCardIconButton } from '../../../components/common/PersonCard/PersonCardSelectable.styled';
import { ExtendedFieldError } from '../../../components/ui/Input/utils';
import { FormTextField } from '../../../components/uikit-components/FormTextField/FormTextField';
import { InputEndAdornmentIcon } from '../../../components/uikit-components/FormTextField/InputEndAdornment.styled';
import {
  ModalContent,
  ModalMain,
  ModalPanel,
  ModalTitle,
} from '../../../components/uikit-components/Modal/Modal.styled';
import { ModalAccessDenied } from '../../../components/uikit-components/Modal/ModalAccessDenied';
import { ModalFooterWithActions } from '../../../components/uikit-components/Modal/ModalFooterWithActions';
import { ModalHeader } from '../../../components/uikit-components/Modal/ModalHeader';
import { ModalHeaderInput } from '../../../components/uikit-components/Modal/ModalHeader.styled';
import { ModalPeopleExtensionPanel } from '../../../components/uikit-components/Modal/ModalPeopleExtensionPanel';
import { getUserTypeTextId } from '../../../helpers/misc';
import useFlag from '../../../hooks/useFlag';
import { useFormError } from '../../../hooks/useFormError';
import useSchoolYears from '../../../hooks/useSchoolYears';
import { GroupPreviewModalInfo } from '../GroupPreviewModal/GroupPreviewModalInfo';
import { resolveAgeGroupsFromIds } from './utils';
import { DeleteHistoryDialog } from './Dialog/DeleteHistoryDialog';
import { GroupSidebarContent } from './GroupSidebarContent';
import { GroupTypeSelect } from './GroupTypeSelect';
import { GroupContent } from './GroupUsersList/GroupContent';
import {
  DEFAULT_LIMITED_TO_SECTION_TEXT_IDS,
  LimitedToCategory,
} from './LimitedToSelect/LimitedToSelect';
import { useLimitedToValidation } from './LimitedToSelect/useLimitedTo';
import { getLimitedToOptions } from './LimitedToSelect/utils';
import { SubjectSelect } from './SubjectSelect/SubjectSelect';
import { AddGroupModalMode, AddGroupModalProps, useAddGroupModal } from './useAddGroupModal';
import { RoomDropdownSelect } from './RoomDropdownSelect';

export enum AddGroupModalSection {
  Header,
  Staff,
  Students,
}

export const AddGroupModal: React.FC<AddGroupModalProps> = (props) => {
  const { formatMessage } = useIntl();
  const { schoolId = '' } = useAuth();
  const {
    subjects,
    actions,
    form,
    groupViewerCanOnlyManageStudents,
    isGroupExpired,
    canEditGroup,
    deleteHistoryUsers,
    removeGroupMembers,
    contextActions,
    isIndividualMember,
    isSavingDisabled,
    facilities,
    isLoadingFacilities,
    selectedRoom,
    groupCanHaveTimetableProps,
    ...contextState
  } = useAddGroupModal({ core: true, ...props });
  const { renderError } = useFormError();

  const schoolPeriodsRangeSelectRef = useRef<SchoolPeriodsRangeSelectComponent>(null);
  const allCategories = getTypedObjectKeys(DEFAULT_LIMITED_TO_SECTION_TEXT_IDS);

  const isMobile = mobileDetect.mobile();

  const studentLimitedToOptions = getLimitedToOptions({
    categories: allCategories,
    schoolProperties: contextState.schoolStudentProperties ?? [],
    subjects: subjects ?? [],
    ageGroups: contextState.ageGroups,
    schoolLevels: contextState.schoolLevels,
  });
  const staffLimitedToOptions = getLimitedToOptions({
    categories: allCategories,
    schoolProperties:
      (contextState.schoolStaffProperties.filter((p) => 'type' in p) as SchoolProperty[]) ?? [],
    subjects: subjects ?? [],
    ageGroups: contextState.ageGroups,
    schoolLevels: contextState.schoolLevels,
  });

  const isGroupTypeDefined = Boolean(
    contextState?.group_type === GroupType.TutorGroup ||
      contextState?.group_type === GroupType.NoSubject ||
      (contextState?.group_type === GroupType.Subject && contextState?.subject?.id),
  );

  const { studentRequiredCategories, hasStudentsLimitedTo } = useMemo(() => {
    const requiredCategories: LimitedToCategory[] = isGroupTypeDefined
      ? ['statuses', 'age_groups']
      : ['statuses'];
    return {
      studentRequiredCategories: requiredCategories,
      hasStudentsLimitedTo: requiredCategories.every((c) =>
        studentLimitedToOptions[c]?.some((o) =>
          contextState.limitedToStudents?.school_property_ids.includes(o.value as string),
        ),
      ),
    };
  }, [
    contextState.limitedToStudents?.school_property_ids,
    isGroupTypeDefined,
    studentLimitedToOptions,
  ]);

  const { staffRequiredCategories, hasStaffLimitedTo } = useMemo(() => {
    const requiredCategories: LimitedToCategory[] = ['statuses'];
    return {
      staffRequiredCategories: requiredCategories,
      hasStaffLimitedTo: requiredCategories.every((c) =>
        staffLimitedToOptions[c]?.some((o) =>
          contextState.limitedToStaff?.school_property_ids.includes(o.value as string),
        ),
      ),
    };
  }, [contextState.limitedToStaff?.school_property_ids, staffLimitedToOptions]);

  const { getConfirmation } = useConfirmationDialog();

  const { defaultValidity } = useSchoolYears();
  const { permissions } = useAuth();
  const isAdmin = permissions.includes('school_admin');
  const isStaffViewer = permissions.includes('staff_viewer');

  const hasActiveAgeGroups = useMemo(
    () => contextState.ageGroups.some((ageGroup) => !ageGroup.archived),
    [contextState.ageGroups],
  );
  const canSelectAgeGroup = hasActiveAgeGroups || contextState.age_group_ids?.length;

  const selectedAgeGroups = useMemo(
    () => resolveAgeGroupsFromIds(contextState.age_group_ids ?? [], contextState.ageGroups ?? []),
    [contextState.age_group_ids, contextState.ageGroups],
  );

  const isEditingLocked = !!contextState.groupId && groupViewerCanOnlyManageStudents;
  const isValidityLocked = isEditingLocked || (isGroupExpired && contextState.has_memberships);

  const [isConfirmationDialogOpen, showConfirmationDialog, hideConfirmationDialog] = useFlag();
  const { isFetching: isStudentValidating, validate: validateStudents } = useLimitedToValidation({
    schoolId: schoolId ?? '',
    userType: 'student',
  });
  const { isFetching: isStaffValidating, validate: validateStaff } = useLimitedToValidation({
    schoolId: schoolId ?? '',
    userType: 'staff',
  });

  const validityChecking = isStaffValidating || isStudentValidating;

  const peopleExtensionDisabled =
    !contextState.name || !contextState.validityRange?.[0] || validityChecking;

  const onSubmit = useCallback(() => {
    if (deleteHistoryUsers?.length) {
      showConfirmationDialog();
      return;
    }
    actions.submitGroup();
  }, [actions, deleteHistoryUsers?.length, showConfirmationDialog]);

  const { deleteHistoryStaff, deleteHistoryStudents } = useMemo(() => {
    const deleteHistoryStaff = contextState.staff
      ?.filter(({ user: { relation_id } }) =>
        deleteHistoryUsers?.some((u) => u.relation_id === relation_id),
      )
      .map(({ user }) => user);

    const deleteHistoryStudents = contextState.students
      ?.filter(({ user: { relation_id } }) =>
        deleteHistoryUsers?.some((u) => u.relation_id === relation_id),
      )
      .map(({ user }) => user);

    return { deleteHistoryStaff, deleteHistoryStudents };
  }, [contextState?.staff, contextState?.students, deleteHistoryUsers]);

  const dateError =
    !!contextState.errors?.validityRange &&
    typeof contextState.errors?.validityRange !== 'string' &&
    !!contextState.errors?.validityRange['values']?.['endDate'];

  const renderCustomIcon = useCallback(
    (userId: string) => {
      return isIndividualMember(userId) ? (
        <ContextMenu key={userId} actions={contextActions(userId)}>
          {(handleClick) => (
            <IconButton inverse onClick={handleClick}>
              <MoreIcon />
            </IconButton>
          )}
        </ContextMenu>
      ) : (
        <PersonCardIconButton onClick={() => actions.handleUserRemove(userId)}>
          <MinusIcon />
        </PersonCardIconButton>
      );
    },
    [actions, contextActions, isIndividualMember],
  );

  const dates = useMemo(() => {
    if (!contextState?.validity) return [];
    const [dateFrom, dateTo] = contextState.validityRange;
    if (!dateFrom || !dateTo) return [];
    return [dateFrom, dateTo];
  }, [contextState?.validity, contextState.validityRange]);

  const validateMembersValidity = useCallback(
    async (dateRange: string[]) => {
      const criteriaMemberIds = contextState?.groupCriteria.flatMap((c) => c.relation_ids ?? []);

      const staffMembers = contextState.groupStaff.length
        ? await validateStaff({
            dateRange,
            criteriaMemberIds: [],
            limitedTo: contextState.limitedToStaff,
            memberIds: contextState.groupStaff.map(({ relation_id }) => relation_id),
          })
        : undefined;

      const studentMembers =
        contextState.groupStudents.length || criteriaMemberIds.length
          ? await validateStudents({
              dateRange,
              criteriaMemberIds: criteriaMemberIds,
              limitedTo: contextState.limitedToStaff,
              memberIds: contextState.groupStudents.map(({ relation_id }) => relation_id),
            })
          : undefined;

      if (!staffMembers && !studentMembers) return;
      return {
        studentMemberIds: studentMembers?.deletedMemberIds,
        staffMemberIds: staffMembers?.deletedMemberIds,
        criteriaIds: studentMembers?.deletedCriteriaMemberIds,
      };
    },
    [
      contextState.groupStudents,
      contextState.groupStaff,
      contextState?.groupCriteria,
      contextState.limitedToStaff,
      validateStaff,
      validateStudents,
    ],
  );

  const handleValidityChange = useCallback(
    async (values: [Date, Date]) => {
      const startDate = format(values[0], DEFAULT_DATE_FORMAT_FNS);
      const endDate = format(values[1], DEFAULT_DATE_FORMAT_FNS);

      const updatedDate = [startDate, endDate];

      if (contextState.focusedField === 'validity') {
        actions.onFieldEditClick();
      }

      if (isEqual(updatedDate, dates)) return;

      if (
        !contextState.groupStudents.length &&
        !contextState.groupStaff.length &&
        !contextState.groupCriteria.length
      ) {
        actions.setValidityRangeHandler({
          date: updatedDate,
        });
        return;
      }

      const membersToRemove = await validateMembersValidity(updatedDate);

      if (!membersToRemove) {
        actions.setValidityRangeHandler({
          date: updatedDate,
        });
        return;
      }

      const { staffMemberIds = [], studentMemberIds = [], criteriaIds = [] } = membersToRemove;

      let errorText = '';

      if (Boolean(staffMemberIds?.length)) {
        const userTypePlural = formatMessage({
          id: getUserTypeTextId('staff', staffMemberIds.length > 1),
        }).toLowerCase();
        errorText = `${staffMemberIds.length} ${userTypePlural}`;
      }

      const onlyCriteriaToRemove = !studentMemberIds?.length && !!criteriaIds?.length;

      if (onlyCriteriaToRemove) {
        const criteriaText = formatMessage({ id: 'groups-AvailableCriteria' }).toLowerCase();
        errorText = errorText ? `${errorText}, ${criteriaText}` : criteriaText;
      }

      if (Boolean(studentMemberIds?.length)) {
        const userTypePlural = formatMessage({
          id: getUserTypeTextId('student', studentMemberIds.length > 1),
        }).toLowerCase();
        const studentText = `${studentMemberIds.length} ${userTypePlural}`;
        errorText = errorText ? `${errorText}, ${studentText}` : studentText;
      }

      const isConfirmed = Boolean(errorText)
        ? await getConfirmation({
            textId: 'groups-LimitedToBulkRemoveWarning',
            textValues: {
              text: errorText,
            },
          })
        : null;

      if (!isConfirmed) {
        schoolPeriodsRangeSelectRef.current?.updateDates(dates);
      } else {
        actions.setValidityRangeHandler({
          date: updatedDate,
        });
        removeGroupMembers({
          studentMemberIds,
          staffMemberIds,
          criteriaIds,
        });
      }
    },
    [
      formatMessage,
      actions,
      contextState.focusedField,
      contextState.groupCriteria,
      contextState.groupStaff?.length,
      contextState.groupStudents?.length,
      dates,
      getConfirmation,
      removeGroupMembers,
      validateMembersValidity,
    ],
  );

  if (!schoolId) return null;

  if (!contextState.isGroupLoading && (!canEditGroup || isGroupExpired)) {
    return (
      <ModalSmall open onClose={actions.closeModal}>
        <ModalHeader active withBorderBottom={false}>
          <IconButton onClick={actions.closeModal}>
            <CrossIcon />
          </IconButton>
        </ModalHeader>
        <ModalContent active>
          <ModalAccessDenied />
        </ModalContent>
      </ModalSmall>
    );
  }

  const renderContent = () => {
    if (contextState.isGroupLoading) {
      return <Loading />;
    }

    return (
      <form onSubmit={form.onSubmit(onSubmit)}>
        {contextState.mode === AddGroupModalMode.Initial && (
          <>
            <ModalHeader
              active
              errorText={renderError(contextState.errors?.name) ?? undefined}
              title={
                isEditingLocked ? (
                  <Stack direction="row" gap={1} sx={{ opacity: 0.5 }}>
                    <ModalTitle style={{ flex: 'none' }}>{contextState.name}</ModalTitle>
                    <InputEndAdornmentIcon>
                      <LockIcon />
                    </InputEndAdornmentIcon>
                  </Stack>
                ) : (
                  <ModalHeaderInput
                    placeholder={formatMessage({ id: 'groups-GroupName' })}
                    value={contextState.name}
                    autoFocus={contextState.focusedField === 'name'}
                    onChange={actions.onGroupNameChange}
                    error={Boolean(contextState.errors?.name)}
                    data-test-id={GROUP.NAME_INPUT}
                  />
                )
              }
            >
              <IconButton data-test-id="modal-close" onClick={actions.closeModal}>
                <CrossIcon />
              </IconButton>
            </ModalHeader>

            <ModalPanel withBorderBottom active sx={{ flexShrink: 1 }}>
              <Stack gap={2.5}>
                <SchoolPeriodsRangeSelect
                  schoolId={schoolId}
                  date={dates}
                  onSetDate={handleValidityChange}
                  hasManagePermission={isAdmin}
                  defaultSchoolYear={defaultValidity}
                  disabled={isValidityLocked}
                  showActualPeriods
                  fromDateError={dateError}
                  toDateError={dateError}
                  ref={schoolPeriodsRangeSelectRef}
                  shouldOpen={contextState.focusedField === 'validity'}
                  isLoading={validityChecking}
                  {...(contextState.errors?.validityRange && {
                    error: {
                      messageTextId: (contextState.errors?.validityRange as MessageDescriptor).id,
                    } as ExtendedFieldError,
                  })}
                  renderHint={
                    contextState.dateNa && (
                      <WithShowOnClick showOnClick={!!isMobile}>
                        {(onClick, show) => (
                          <Tooltip
                            open={show}
                            title={formatMessage({ id: 'groups-NoCurrentSchoolYear-ValidityHint' })}
                          >
                            <Stack
                              onClick={onClick}
                              sx={(theme) => ({
                                position: 'absolute',
                                top: theme.spacing(1.5),
                                right: theme.spacing(5),
                                color: 'warning.main',
                                fontSize: theme.spacing(2.5),
                                zIndex: theme.zIndex.drawer,

                                '& .svg-icon': { '& circle, & rect': { color: 'common.white' } },
                              })}
                            >
                              <Attention2Icon />
                            </Stack>
                          </Tooltip>
                        )}
                      </WithShowOnClick>
                    )
                  }
                />

                {!isEditingLocked && (
                  <GroupTypeSelect
                    selectedType={contextState.group_type}
                    onSelectType={actions.setGroupType}
                  />
                )}

                {contextState.group_type === GroupType.Subject && (
                  <SubjectSelect isEditingLocked={isEditingLocked} />
                )}

                <Stack gap={1} data-scope>
                  <Typography variant="h2">
                    {formatMessage({ id: 'groups-SectionHeader-ForWhom' })}
                  </Typography>
                  <Typography variant="h3" color="text.primary">
                    {formatMessage({ id: 'groups-SectionHeader-ForWhom-Description' })}
                  </Typography>

                  {contextState.group_type === GroupType.NoSubject ? (
                    <AgeGroupSelectMultiple
                      schoolId={schoolId}
                      isAdmin={isAdmin}
                      userRole={SchoolUserRole.Student}
                      selectedIds={contextState.age_group_ids}
                      onSelect={actions.handleAgeGroupsSelect}
                      placeholder={formatMessage({ id: 'groups-GroupAgeGroup' })}
                      requiredLabel="required"
                      disabled={isEditingLocked || !canSelectAgeGroup}
                      error={contextState.errors?.age_group_ids ? { type: 'required' } : undefined}
                      shouldOpen={contextState.focusedField === 'age_groups'}
                      testId={DROPDOWN.AGE_GROUP}
                    />
                  ) : (
                    <AgeGroupSelect
                      schoolId={schoolId}
                      isAdmin={isAdmin}
                      userRole={SchoolUserRole.Student}
                      selectedId={contextState.age_group_ids[0]}
                      onSelect={actions.handleAgeGroupSelect}
                      placeholder={formatMessage({ id: 'groups-GroupAgeGroup' })}
                      requiredLabel="required"
                      disabled={isEditingLocked || !canSelectAgeGroup}
                      error={contextState.errors?.age_group_ids ? { type: 'required' } : undefined}
                      shouldOpen={contextState.focusedField === 'age_groups'}
                      testId={DROPDOWN.AGE_GROUP}
                    />
                  )}
                </Stack>

                <Stack gap={1}>
                  <Typography variant="h2">
                    {formatMessage({ id: 'groups-SectionHeader-OtherDetails' })}
                  </Typography>
                  {groupCanHaveTimetableProps && (
                    <Stack direction="row" gap={2.5} mb={1} sx={{ '> *': { flex: 1 } }}>
                      <FormTextField
                        label={formatMessage({ id: 'groups-TimetableCode' })}
                        customIcon={
                          <Tooltip
                            title={formatMessage({ id: 'groups-TimetableCode-Tooltip' })}
                            componentsProps={{ tooltip: { sx: { p: 1.25 } } }}
                          >
                            <IconButton>
                              <InformationIcon />
                            </IconButton>
                          </Tooltip>
                        }
                        fullWidth
                        onChange={actions.onTimetableCodeChange}
                        onBlur={actions.handleTimetableCodeBlur}
                        value={contextState.timetable_code ?? ''}
                        error={Boolean(contextState.errors?.timetable_code)}
                        helperText={renderError(contextState.errors?.timetable_code)}
                        data-test-id={GROUP.TIMETABLE_CODE_INPUT}
                      />

                      <RoomDropdownSelect
                        selectedRoom={selectedRoom}
                        facilities={facilities}
                        isLoading={isLoadingFacilities}
                        onSelect={actions.handleDefaultRoomSelect}
                      />
                    </Stack>
                  )}

                  <FormTextField
                    label={formatMessage({ id: 'groups-GroupDescription' })}
                    multiline
                    fullWidth
                    minRows={2}
                    autoFocus={contextState.focusedField === 'description'}
                    onChange={actions.onInputChangeHandler('description')}
                    value={contextState.description}
                    data-test-id={GROUP.DESCRIPTION_INPUT}
                  />
                </Stack>
              </Stack>
            </ModalPanel>
          </>
        )}

        {contextState.mode !== AddGroupModalMode.Initial && (
          <GroupPreviewModalInfo
            group={contextState as unknown as GroupWithMembers}
            validity={
              contextState.validityRange
                ? `${format(
                    newDateTimezoneOffset(contextState.validityRange[0]),
                    DATE_FORMAT,
                  )} - ${format(newDateTimezoneOffset(contextState.validityRange[1]), DATE_FORMAT)}`
                : ''
            }
            ageGroups={selectedAgeGroups}
            onModalClose={actions.closeModal}
            onEditClick={actions.onFieldEditClick}
            locked={isEditingLocked}
            titleErrorText={renderError(contextState.errors?.name) ?? undefined}
          />
        )}
        <ModalPeopleExtensionPanel
          titleId="section-Staff"
          addActionId="groups-AddStaff"
          editActionId="groups-EditStaff"
          count={contextState.groupStaff?.length}
          active={contextState.mode === AddGroupModalMode.Staff && !isConfirmationDialogOpen}
          disabled={peopleExtensionDisabled}
          onAddClick={actions.setStateHandler('mode', AddGroupModalMode.Staff)}
          actionButton={
            !peopleExtensionDisabled &&
            !isStaffViewer && (
              <Tooltip title={formatMessage({ id: 'groups-NoManageStaffPermissions' })}>
                <Icon>
                  <LockIcon />
                </Icon>
              </Tooltip>
            )
          }
          testId="groups-modal-add-staff"
          sidebarContent={
            hasStaffLimitedTo ? (
              <GroupSidebarContent
                deleteHistoryUsers={deleteHistoryStaff}
                userType="staff"
                schoolId={schoolId}
                titleId="userType-staff"
                groupCriteria={contextState.groupCriteria}
                groupUsers={contextState.groupStaff}
                onUserClick={actions.handleUserAdd}
                onAddUsers={actions.handleUsersAdd}
                onCriteriaClick={actions.handleCriteriaClick}
                groupId={contextState.groupId}
                limitedTo={contextState.limitedToStaff}
                validityRange={contextState.validityRangeRequest}
                onRemovedUserClick={actions.handleUserRecover}
                activeTab={contextState.activeTab}
              />
            ) : undefined
          }
        >
          <ModalContent withBorderBottom active flat>
            <GroupContent
              userType="staff"
              limitedTo={contextState.limitedToStaff}
              onLimitedToChange={(d, e) => actions.handleLimitedToChange(d, 'staff', e)}
              groupUsers={contextState.groupStaff}
              renderCustomIcon={renderCustomIcon}
              schoolId={schoolId}
              dateRange={contextState.validityRangeRequest}
              limitedToOptions={staffLimitedToOptions}
              requiredCategories={staffRequiredCategories}
            />
          </ModalContent>
        </ModalPeopleExtensionPanel>

        <ModalPeopleExtensionPanel
          titleId="section-Students"
          addActionId="groups-AddStudents"
          editActionId="groups-EditStudents"
          count={contextState.uniqStudentsCount}
          active={contextState.mode === AddGroupModalMode.Students && !isConfirmationDialogOpen}
          disabled={peopleExtensionDisabled}
          onAddClick={actions.openStudentsMode}
          forceShowEditButton={contextState.groupCriteria?.length > 0}
          testId="groups-modal-edit-students"
          sidebarContent={
            hasStudentsLimitedTo ? (
              <GroupSidebarContent
                deleteHistoryUsers={deleteHistoryStudents}
                schoolId={schoolId}
                userType="student"
                titleId="userType-student-plural"
                groupCriteria={contextState.groupCriteria}
                groupUsers={contextState.groupStudents}
                onUserClick={actions.handleUserAdd}
                onAddUsers={actions.handleUsersAdd}
                onCriteriaClick={actions.handleCriteriaClick}
                groupId={contextState.groupId}
                limitedTo={contextState.limitedToStudents}
                validityRange={contextState.validityRangeRequest}
                onRemovedUserClick={actions.handleUserRecover}
                activeTab={contextState.activeTab}
              />
            ) : undefined
          }
        >
          <ModalContent active flat>
            <GroupContent
              userType="student"
              limitedTo={contextState.limitedToStudents}
              onLimitedToChange={(d, e) => actions.handleLimitedToChange(d, 'student', e)}
              groupUsers={contextState.groupStudents}
              groupCriteria={contextState.groupCriteria}
              onCriteriaClick={actions.handleCriteriaClick}
              renderCustomIcon={renderCustomIcon}
              schoolId={schoolId}
              dateRange={contextState.validityRangeRequest}
              limitedToOptions={studentLimitedToOptions}
              requiredCategories={studentRequiredCategories}
              groupAgeGroupIds={contextState.age_group_ids}
            />
          </ModalContent>
        </ModalPeopleExtensionPanel>

        <ModalMain />

        <ModalFooterWithActions
          saving={contextState.saving}
          deleting={contextState.deleting}
          disabled={isSavingDisabled || validityChecking}
          showDeleteButton={!!contextState.groupId}
          isNewItem={!contextState.groupId}
          onDeleteClick={actions.removeGroup}
        />
      </form>
    );
  };

  return (
    <ModalSmall open onClose={actions.closeModal}>
      {renderContent()}
      <DeleteHistoryDialog
        deleteHistoryStudents={deleteHistoryStudents}
        deleteHistoryStaff={deleteHistoryStaff}
        isOpen={isConfirmationDialogOpen}
        onClose={hideConfirmationDialog}
        onConfirm={actions.submitGroup}
        loading={contextState.saving}
      />
    </ModalSmall>
  );
};
